import {
  Button,
  DateFormats,
  Dropdown,
  formatISODate,
  Inline,
  type OptionType,
  type SingleValue,
  Stack,
  Table,
  ToggleSwitch,
} from '@omnipresentgroup/design-system';
import {
  useInvoiceReport,
  useInvoiceReportEmployeeBreakdown,
} from '@omnipresentgroup/domains';
import type {
  InvoiceReportEmployee,
  InvoiceReportEmployeeBreakdownItem,
} from '@omnipresentgroup/types';
import {
  type CoreRow,
  getCoreRowModel,
  getExpandedRowModel,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { WrappedMixpanel } from '../../mixpanelWrapper';
import { useInvoiceReportEmployeeBreakdownTableConfig } from './hooks';

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const EMPTY_ARRAY: InvoiceReportEmployeeBreakdownItem[] = [];
const LOADING_ARRAY = Array.from(
  { length: 10 },
  () => ({}) as InvoiceReportEmployeeBreakdownItem,
);

type Props = {
  invoiceId: string;
  companyId: string;
  employee: InvoiceReportEmployee;
  userId: string;
};

export const InvoiceReportEmployeeBreakdown = ({
  companyId,
  invoiceId,
  employee,
  userId,
}: Props) => {
  const [showValuesInLocalCurrency, setShowValuesInLocalCurrency] =
    useState<boolean>(false);
  const [previousInvoiceIdToCompare, setPreviousInvoiceIdToCompare] = useState<
    string | null
  >(null);

  const queryProps = useMemo(
    () => ({
      showValuesInLocalCurrency,
      previousInvoiceIdToCompare,
    }),
    [showValuesInLocalCurrency, previousInvoiceIdToCompare],
  );

  const { data: invoice, isLoading: isInvoiceLoading } = useInvoiceReport({
    companyId,
    invoiceId: invoiceId || '',
    enabled: !!invoiceId,
  });

  const {
    data: invoiceReportEmployeeBreakdownResult,
    isLoading: isInvoiceEmployeeBreakdownLoading,
    isError,
  } = useInvoiceReportEmployeeBreakdown({
    companyId: companyId,
    invoiceId: invoiceId,
    employeeId: employee.id,
    showLocalCurrency: showValuesInLocalCurrency,
    invoiceIdToCompare: previousInvoiceIdToCompare || undefined,
  });

  const canSwitchCurrencies = useMemo(
    () =>
      employee.billedAmountExcludingFees &&
      employee.localAmountExcludingFees &&
      employee.billedAmountExcludingFees.currency !==
        employee.localAmountExcludingFees.currency,
    [employee.billedAmountExcludingFees, employee.localAmountExcludingFees],
  );

  const { columns } = useInvoiceReportEmployeeBreakdownTableConfig({
    totalAmount: invoiceReportEmployeeBreakdownResult?.totalAmount,
    comparedTotalAmount:
      invoiceReportEmployeeBreakdownResult?.comparedInvoice?.totalAmount,
    comparedPayPeriod:
      invoiceReportEmployeeBreakdownResult?.comparedInvoice?.payPeriod,
  });

  const tableOptions: Omit<
    TableOptions<InvoiceReportEmployeeBreakdownItem>,
    'data' | 'columns'
  > = {
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.items,
  };

  const isLoading = useMemo(
    () => isInvoiceEmployeeBreakdownLoading || isInvoiceLoading,
    [isInvoiceEmployeeBreakdownLoading, isInvoiceLoading],
  );

  const table = useReactTable({
    data: isLoading
      ? LOADING_ARRAY
      : (invoiceReportEmployeeBreakdownResult?.items ?? EMPTY_ARRAY),
    columns: columns,
    ...tableOptions,
  });

  const previousInvoices: OptionType[] | undefined = useMemo(() => {
    const options = employee.prevInvoices?.map((item) => ({
      label: `${formatISODate(item.payPeriod, DateFormats.DateWithoutDay)}`,
      value: item.id,
    }));

    return options
      ? [
          {
            label: 'No comparison',
            value: '',
          },
          ...options,
        ]
      : undefined;
  }, [employee.prevInvoices]);

  return (
    <Stack>
      <Inline css={{ flexWrap: 'wrap' }} align="end">
        {employee.prevInvoices && employee.prevInvoices.length > 0 && (
          <Dropdown
            id="previous-invoice-dropdown"
            label="Compare with previous invoice"
            placeholder="Choose an option"
            size="small"
            minMenuWidth={150}
            onChange={(newValue) => {
              const previousInvoice = newValue as SingleValue<OptionType>;

              if (previousInvoice?.value) {
                WrappedMixpanel.track('Select Invoice Comparison', {
                  companyId,
                  userId,
                  invoiceId: invoice?.id || invoiceId,
                  memo: invoice?.reference,
                  comparedInvoicePayPeriod: previousInvoice?.label,
                });
              }

              setPreviousInvoiceIdToCompare(previousInvoice?.value as string);
            }}
            options={
              previousInvoices
                ? [
                    {
                      options: previousInvoices,
                    },
                  ]
                : []
            }
            isDisabled={isInvoiceEmployeeBreakdownLoading}
          />
        )}
        {canSwitchCurrencies && (
          <ToggleSwitch
            id="local-currency-toggle-switch"
            label="Show values in local currency"
            size="small"
            checked={queryProps.showValuesInLocalCurrency}
            onCheckedChange={(checked) => {
              if (checked) {
                WrappedMixpanel.track('Toggle Local Currency On', {
                  companyId,
                  userId,
                  invoiceId: invoice?.id || invoiceId,
                  memo: invoice?.reference,
                });
              }
              setShowValuesInLocalCurrency(checked);
            }}
          />
        )}
      </Inline>
      <Table
        id="invoice-reports-list-breakdown"
        table={table}
        loading={isLoading}
        errorStateProps={{
          id: 'invoice-reports-list-error',
          title: ERROR_STATE_TITLE,
          description: ERROR_STATE_DESCRIPTION,
          callToAction: <Button onClick={window.location.reload}>Retry</Button>,
        }}
        showErrorState={isError}
        showHeader={false}
        useRowExpansion
        showFooter
        expandAllRows
        onRowExpanded={(row: CoreRow<InvoiceReportEmployeeBreakdownItem>) => {
          WrappedMixpanel.track('Breakdown Clicked', {
            companyId,
            userId,
            invoiceId: invoice?.id || invoiceId,
            memo: invoice?.reference,
            billingItem: row.original.label,
          });
        }}
      />
    </Stack>
  );
};
